import { useEffect } from 'react';
import {
    ScrollProgressBar,
    HeroSection,
    BuildingBlueprintHero,
    DetectionSection,
    EquipmentSection,
    AlertGridSection,
    TimelineSection,
    StatsSection,
    ComplianceDemo,
    CTASection,
    SystemStatusScrubber,
} from '../components/landing';
import { Flame } from 'lucide-react';

/**
 * Landing Page - Marketing page with scroll-based animations.
 * Based on ENHANCED_SYSTEM_PROMPT.md specifications.
 * 
 * Features:
 * - Hero section with word-stagger animation + blueprint grid
 * - Building blueprint background with scroll-driven states
 * - Detection section with pulsing detector visual
 * - Alert network with 3x3 grid animation
 * - Response timeline with sequential reveal
 * - Statistics with animated counters + circuit board connections
 * - Interactive compliance demo
 * - CTA with gradient background
 * - Scroll progress indicator
 */
export default function LandingPage() {
    // Set page title
    useEffect(() => {
        document.title = 'Environaise - Risk Analysis Bureau';

        // Add meta description for SEO
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content',
                'Premier risk analysis bureau for engineers and architects. Deep-dive risk assessments and compliance reports to ensure your designs meet international safety standards.'
            );
        }
    }, []);

    return (
        <div className="min-h-screen relative" style={{ background: 'var(--dark-bg)' }}>
            {/* Scroll Progress Bar */}
            <ScrollProgressBar />

            {/* Building Blueprint Background (scroll-driven) */}
            <BuildingBlueprintHero />

            {/* System Status Scrubber (scroll indicator) */}
            <SystemStatusScrubber />

            {/* Vignette Overlay - HUD Effect */}
            <div className="vignette-overlay" aria-hidden="true" />

            {/* Fixed Header */}
            <header className="fixed top-0 left-0 right-0 z-40 px-6 py-4 backdrop-blur-lg bg-[var(--dark-bg)]/80 border-b border-white/5">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[var(--fire-orange)] to-[var(--fire-red)] flex items-center justify-center">
                            <Flame className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xl font-display font-bold text-white">Environaise</span>
                    </div>

                    {/* Navigation */}
                    <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
                        <a href="#detection-section" className="text-gray-400 hover:text-white transition-colors">
                            Features
                        </a>
                        <a href="#stats-section" className="text-gray-400 hover:text-white transition-colors">
                            About
                        </a>
                        <a href="/login" className="text-gray-400 hover:text-white transition-colors">
                            Sign In
                        </a>
                        <a
                            href="/register"
                            className="px-5 py-2 rounded-lg font-medium transition-all hover:scale-105"
                            style={{ background: 'var(--gradient-fire)', color: 'white' }}
                        >
                            Get Started
                        </a>
                    </nav>

                    {/* Mobile menu button */}
                    <button
                        className="md:hidden p-2 text-gray-400 hover:text-white"
                        aria-label="Open menu"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="relative z-10">
                <HeroSection />
                <DetectionSection />
                <EquipmentSection />
                <AlertGridSection />
                <TimelineSection />
                <StatsSection />
                <ComplianceDemo />
                <CTASection />
            </main>

            {/* Footer */}
            <footer
                className="py-12 px-4 border-t border-white/5"
                style={{ background: 'var(--dark-bg)' }}
            >
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        {/* Logo */}
                        <a href="/" className="flex items-center gap-2 text-white font-display font-bold text-xl">
                            <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center"
                                style={{ background: 'var(--gradient-fire)' }}
                            >
                                <Flame className="w-4 h-4 text-white" />
                            </div>
                            <span>Environaise</span>
                        </a>

                        {/* Links */}
                        <nav className="flex flex-wrap justify-center gap-6 text-sm text-gray-400" aria-label="Footer navigation">
                            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
                            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
                            <a href="#" className="hover:text-white transition-colors">Contact</a>
                            <a href="#" className="hover:text-white transition-colors">Compliance</a>
                        </nav>

                        {/* Copyright */}
                        <p className="text-sm text-gray-500">
                            © {new Date().getFullYear()} Environaise. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
